import { useMemo } from 'react';
import { useRouterState } from '@tanstack/react-router';
import type { ProductPlan } from '@/features/products/types';
import { useConfig } from '@/features/config';
import visaLogo from '../assets/logo-visa.svg';
import mastercardLogo from '../assets/logo-mastercard.svg';
import maestroLogo from '../assets/logo-maestro.svg';
import applePayMark from '../assets/logo-apple-pay-mark.svg';
import shieldIcon from '../assets/icon-shield.svg';
import { PlanCard } from './plan-card';
import { PlanCardV2 } from './plan-card-v2';
import { PlanPriceRow } from './plan-price-row';
import { PaymentButtons } from './payment-buttons';
import { buildConsentDisclaimer, buildRenewalDisclaimer } from '../domain/disclaimer';
import { resolvePlansVariant } from '../plans-variant-registry';

interface CheckoutPlansProps {
  plans: ProductPlan[];
  selectedPlan: ProductPlan;
  onSelect: (plan: ProductPlan) => void;
  applePayAvailable: boolean;
  onApplePay: () => void;
  onCard: () => void;
}

export function CheckoutPlans({
  plans,
  selectedPlan,
  onSelect,
  applePayAvailable,
  onApplePay,
  onCard,
}: CheckoutPlansProps) {
  const singlePlan = plans.length === 1;
  const { params: configParams } = useConfig();
  const search = useRouterState({ select: (s) => s.location.search }) as Record<string, unknown>;
  const plansVariant = useMemo(() => {
    const plansV =
      (typeof search.plans_v === 'string' ? search.plans_v : undefined) ?? configParams.plans_v;
    return resolvePlansVariant({ plans_v: plansV }).id;
  }, [search, configParams.plans_v]);

  return (
    <section id="checkout-plans" className="flex scroll-mt-[72px] flex-col gap-4 px-4">
      {singlePlan ? (
        <PlanPriceRow plan={selectedPlan} />
      ) : (
        <>
          <h2 className="text-center font-display text-[28px] font-bold leading-9 text-mf-neutral-900">
            Choose your plan
          </h2>

          <div className="flex flex-col gap-2">
            {plans.map((plan, index) =>
              plansVariant === 'v2' ? (
                <PlanCardV2
                  key={plan.id}
                  plan={plan}
                  selected={selectedPlan.id === plan.id}
                  onSelect={() => onSelect(plan)}
                  index={index}
                  total={plans.length}
                />
              ) : (
                <PlanCard
                  key={plan.id}
                  plan={plan}
                  selected={selectedPlan.id === plan.id}
                  onSelect={() => onSelect(plan)}
                />
              ),
            )}
          </div>
        </>
      )}

      <PaymentButtons
        applePayAvailable={applePayAvailable}
        onApplePay={onApplePay}
        onCard={onCard}
      />

      <div className="flex flex-col items-center gap-4 rounded-[24px] border border-mf-neutral-100 bg-white p-4">
        <div className="flex w-full flex-col items-center gap-2">
          <p className="text-sm font-bold leading-4 text-mf-neutral-900">Safe &amp; Secure Payment</p>
          <div className="flex w-full gap-1">
            <PaymentLogo src={visaLogo} alt="Visa" className="h-3 w-[37px]" />
            <PaymentLogo src={mastercardLogo} alt="Mastercard" className="h-6 w-[31px]" />
            <PaymentLogo src={maestroLogo} alt="Maestro" className="h-6 w-[31px]" />
            <PaymentLogo src={applePayMark} alt="Apple Pay" className="h-4 w-[38px]" />
          </div>
        </div>
        <div className="h-px w-full bg-mf-neutral-100" />
        <div className="flex w-full flex-col items-center gap-2">
          <div className="flex items-center justify-center gap-1 rounded-[20px] bg-mf-neutral-50 px-4 py-2">
            <img src={shieldIcon} alt="" className="size-[18px] object-contain" />
            <span className="text-sm font-bold leading-4 text-mf-neutral-900">
              100% money back guarantee
            </span>
          </div>
          <p className="text-center text-xs leading-4 text-mf-neutral-400">
            {singlePlan
              ? buildConsentDisclaimer(selectedPlan)
              : buildRenewalDisclaimer(selectedPlan)}
          </p>
        </div>
      </div>
    </section>
  );
}

function PaymentLogo({
  src,
  alt,
  className,
}: {
  src: string;
  alt: string;
  className: string;
}) {
  return (
    <div className="flex h-10 flex-1 items-center justify-center rounded-lg bg-mf-neutral-50">
      <img src={src} alt={alt} className={className} />
    </div>
  );
}
